import React from 'react';
import { ButtonProps } from '@/types/components';
import { ButtonVariant, ButtonSize } from '@/types/common';
import { Button as KitButton } from '@/design-system/button';
import { cn } from '@/design-system/lib/utils';

/**
 * App Button — same public API as before, rendered by the design-system
 * Button (src/design-system/button.tsx).
 *
 * Per GSM, the kit's primary filled button is an INK surface — purple is
 * punctuation, never a button fill. `secondary` keeps the soft purple tint.
 */

type KitTone = 'primary' | 'neutral' | 'destructive';
type KitStyle = 'filled' | 'stroke' | 'lighter' | 'ghost';

const VARIANT_MAP: Record<
  ButtonVariant,
  { tone: KitTone; style: KitStyle; className?: string }
> = {
  primary: { tone: 'primary', style: 'filled' },
  secondary: { tone: 'primary', style: 'lighter' },
  danger: { tone: 'destructive', style: 'filled' },
  neutral: { tone: 'neutral', style: 'lighter' },
  white: { tone: 'neutral', style: 'stroke' },
  'outline-accent': { tone: 'primary', style: 'stroke' },
  'outline-danger': { tone: 'destructive', style: 'stroke' },
  'outline-neutral': { tone: 'neutral', style: 'stroke' },
  ghost: { tone: 'neutral', style: 'ghost' },
  // Operational-status outlines — no kit tone exists for these; keep them as
  // neutral strokes recolored with the state ramps (information, not brand).
  'outline-success': {
    tone: 'neutral',
    style: 'stroke',
    className:
      'border-(--dash-green-300) text-(--state-success-dark) hover:bg-(--state-success-lighter) hover:text-(--state-success-dark)',
  },
  'outline-warning': {
    tone: 'neutral',
    style: 'stroke',
    className:
      'border-(--dash-orange-300) text-(--state-warning-dark) hover:bg-(--state-warning-lighter) hover:text-(--state-warning-dark)',
  },
  'outline-info': {
    tone: 'neutral',
    style: 'stroke',
    className:
      'border-(--dash-sky-300) text-(--state-verified-dark) hover:bg-(--state-verified-lighter) hover:text-(--state-verified-dark)',
  },
};

const SIZE_MAP: Record<ButtonSize, 'xs' | 'sm' | 'md' | 'xl'> = {
  xs: 'xs',
  small: 'sm',
  medium: 'md',
  large: 'xl',
};

export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'medium',
  disabled = false,
  className = '',
  // Explicit passthrough, not a `...rest` spread: the kit Button takes a
  // curated prop set, and spreading would let arbitrary props reach it. These
  // two have to survive because specs select on them and screen readers read
  // them — a dropped data-testid fails silently, which is the worst kind.
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}) => {
  const mapped = VARIANT_MAP[variant ?? 'primary'] ?? VARIANT_MAP.primary;

  return (
    <KitButton
      type={type}
      tone={mapped.tone}
      style={mapped.style}
      size={SIZE_MAP[size]}
      disabled={disabled}
      onClick={(e) => onClick?.(e)}
      className={cn(mapped.className, className)}
      data-testid={dataTestId}
      aria-label={ariaLabel}
    >
      {children}
    </KitButton>
  );
};

export default Button;
